/** Safe billing / request-trace helpers for Staging diagnosis. Never log PII or prompts. */

export type BillingPath = 'ledger' | 'legacy';

export type BillingOutcome =
  | 'charged' |'replayed' |'refunded' |'insufficient' |'failed' |'completed';

export interface BillingTraceFields {
  requestId: string;
  caller: string;
  feature: string;
  operationKey: string;
  billingPath: BillingPath;
  hasIdempotencyKey: boolean;
  cost: number;
  billingOutcome: BillingOutcome;
  providerCallStarted: boolean;
  providerCallCompleted: boolean;
  timestamp: string;
}

const REQUEST_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const CALLER_RE = /^[a-z0-9][a-z0-9:_-]{0,79}$/i;

export function isValidRequestId(value: string): boolean {
  return REQUEST_ID_RE.test(value);
}

export function sanitizeCaller(raw: unknown): string {
  if (typeof raw !== 'string') return 'unknown-legacy-caller';
  const trimmed = raw.trim().slice(0, 80);
  if (CALLER_RE.test(trimmed)) return trimmed;
  return 'unknown-legacy-caller';
}

export function sanitizeFeature(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim().slice(0, 64);
  if (/^[a-z0-9][a-z0-9_-]{0,63}$/i.test(trimmed)) return trimmed;
  return '';
}

export function sanitizeOperationKey(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return 'chat-completion';
  const trimmed = raw.trim().slice(0, 64);
  if (/^[a-z0-9][a-z0-9_-]{0,63}$/i.test(trimmed)) return trimmed;
  return 'chat-completion';
}

export function resolveClientRequestId(candidate?: string): string {
  if (candidate && isValidRequestId(candidate.trim())) return candidate.trim();
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Client Staging/test console diagnostics only — never shown in UI. */
export function shouldLogClientBillingDiagnostics(): boolean {
  return process.env.NEXT_PUBLIC_TEST_MODE === 'true';
}

export function logClientBillingDiagnostics(payload: Record<string, unknown>): void {
  if (!shouldLogClientBillingDiagnostics()) return;
  if (typeof console === 'undefined' || typeof console.info !== 'function') return;
  console.info('[ai-billing-trace]', payload);
}

export function logServerBillingTrace(trace: BillingTraceFields): void {
  // Production (TEST_MODE=false) must not emit diagnostic traces.
  if (process.env.NEXT_PUBLIC_TEST_MODE !== 'true') return;
  // Structured single-line object — safe fields only.
  console.info('[ai-billing-trace]', {
    requestId: trace.requestId,
    caller: trace.caller,
    feature: trace.feature,
    operationKey: trace.operationKey,
    billingPath: trace.billingPath,
    hasIdempotencyKey: trace.hasIdempotencyKey,
    cost: trace.cost,
    billingOutcome: trace.billingOutcome,
    providerCallStarted: trace.providerCallStarted,
    providerCallCompleted: trace.providerCallCompleted,
    timestamp: trace.timestamp,
  });
}
